import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

type Props = {
  /** 表示するテキスト（見切れた場合はホバー/フォーカスで全文をポップアップ表示する） */
  text: string;
  /** truncate される要素（<p>）に付与する追加クラス。幅制約（max-w-*）やフォント指定をここで渡す */
  className?: string;
  /** ポップアップ側に付与する追加クラス */
  tooltipClassName?: string;
};

/** ツールチップと画面端の最小マージン */
const VIEWPORT_MARGIN = 8;
/** ホバー対象とツールチップの間隔 */
const ANCHOR_GAP = 8;
/** 矢印の幅（w-3）の半分 */
const ARROW_HALF_WIDTH = 6;

type Position = {
  left: number;
  top: number;
  placement: 'top' | 'bottom';
  arrowLeft: number;
};

/**
 * テキストが CSS truncate で見切れている場合のみ、ホバー/フォーカスで全文を
 * 黒背景の吹き出しとして表示する共通コンポーネント。
 *
 * 従来は候補者カード等に同じロジックがインライン重複していたため共通化した。
 * scrollWidth > clientWidth で「実際に見切れているか」を判定し、
 * 見切れている時だけツールチップとキーボードフォーカスを有効化する。
 *
 * 吹き出しは body へ portal + position: fixed で描画する。祖先の overflow-y-auto
 * （モーダル本文など）に切り取られたりスクロールを誘発したりするのを避けるため。
 */
export function OverflowTooltip({ text, className, tooltipClassName }: Props) {
  const ref = useRef<HTMLParagraphElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);

  // スクロール幅と表示幅を比較して省略されているかを判定
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return false;
    const truncated = el.scrollWidth > el.clientWidth;
    setIsTruncated(truncated);
    return truncated;
  }, []);

  const openTooltip = useCallback(() => {
    setPosition(null);
    setShowTooltip(measure());
  }, [measure]);

  const closeTooltip = useCallback(() => setShowTooltip(false), []);

  // マウント直後だけの測定ではタブ切替・モーダル展開前のレイアウトで
  // 判定が固定されてしまうため、幅の変化も監視して測り直す
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, measure]);

  // 実寸を測ってからビューポート内へ収める。下に入らなければ上へ反転させる
  useLayoutEffect(() => {
    if (!showTooltip) return;

    const updatePosition = () => {
      const anchor = ref.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;

      const anchorRect = anchor.getBoundingClientRect();
      const { width, height } = tooltip.getBoundingClientRect();

      const overflowsBottom =
        anchorRect.bottom + ANCHOR_GAP + height > window.innerHeight - VIEWPORT_MARGIN;
      const fitsTop = anchorRect.top - ANCHOR_GAP - height > VIEWPORT_MARGIN;
      const placement = overflowsBottom && fitsTop ? 'top' : 'bottom';

      const top =
        placement === 'top' ? anchorRect.top - ANCHOR_GAP - height : anchorRect.bottom + ANCHOR_GAP;

      const anchorCenter = anchorRect.left + anchorRect.width / 2;
      const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width);
      const left = Math.min(Math.max(VIEWPORT_MARGIN, anchorCenter - width / 2), maxLeft);

      setPosition({ left, top, placement, arrowLeft: anchorCenter - left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip, text]);

  return (
    <div className="min-w-0">
      <p
        ref={ref}
        className={clsx('truncate', className)}
        // 見切れている時だけキーボードフォーカス可能にする
        tabIndex={isTruncated ? 0 : undefined}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
      >
        {text}
      </p>
      {showTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            className={clsx(
              // 画面幅に追従させる（狭い画面でははみ出さず、広い画面では読みやすい上限で止める）
              'pointer-events-none fixed z-[1200] w-max max-w-[min(60vw,900px)] rounded-xs bg-black p-sm shadow-lg',
              tooltipClassName
            )}
            style={
              position
                ? { left: position.left, top: position.top }
                : { left: 0, top: 0, visibility: 'hidden' }
            }
          >
            {position && (
              <div
                className="absolute h-2 w-3 bg-black"
                style={{
                  left: position.arrowLeft - ARROW_HALF_WIDTH,
                  top: position.placement === 'bottom' ? -6 : undefined,
                  bottom: position.placement === 'top' ? -6 : undefined,
                  clipPath:
                    position.placement === 'bottom'
                      ? 'polygon(45.45% 0%, 0% 100%, 100% 100%)'
                      : 'polygon(0% 0%, 100% 0%, 54.55% 100%)',
                }}
              />
            )}
            <p className="text-xs leading-[170%] break-words text-white">{text}</p>
          </div>,
          document.body
        )}
    </div>
  );
}
